import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { applySeo } from "@/lib/seo";
import { AgencyLogo } from "@/components/AgencyLogo";
import { BarometerNav } from "@/components/BarometerNav";
import { BarometerHero } from "@/components/BarometerHero";
import { trpc } from "@/lib/trpc";
import { BAROMETER_PRICES, BAROMETER_MIT_VIDEOCALL, formatPrice } from "@shared/prices";
import { toast } from "sonner";
import { CrossSelling } from "@/components/CrossSelling";
import { generateConversionPdf } from "@/lib/generatePdf";

const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";
const MEETERGO_URL = "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko?utm_source=ig&utm_medium=social&utm_content=link_in_bio";

// Conversion-Barometer Akzentfarbe: Warmes Amber/Orange – steht für Energie, Handlung, Dringlichkeit
const C = {
  amber: "#b45309",       // Amber-700
  amberDark: "#78350f",   // Amber-900
  amberLight: "#b4530912",
  amberMid: "#d97706",    // Amber-600
  bordeaux: "#b45309",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
  border: "#e5e7eb",
};

const BRANCHEN = [
  { value: "", label: "Branche wählen (optional)" },
  { value: "dienstleister", label: "Dienstleister / Beratung" },
  { value: "ecommerce", label: "E-Commerce / Online-Shop" },
  { value: "gastronomie", label: "Gastronomie / Hotel / Tourismus" },
  { value: "gesundheit", label: "Gesundheit / Wellness / Beauty" },
  { value: "b2b", label: "B2B / Software / Technologie" },
  { value: "handwerk", label: "Handwerk / Bau / Immobilien" },
  { value: "kreativ", label: "Kreativbranche / Agentur / Design" },
  { value: "sonstige", label: "Sonstige" },
];

type ConversionDimension = {
  name: string;
  score: number;
  max: number;
  icon: string;
  findings: string[];
  recommendations: string[];
};

type ConversionData = {
  url: string;
  branche: string;
  total_score: number;
  max_score: number;
  grade: string;
  percentage: number;
  screenshot_b64: string | null;
  dimensions: ConversionDimension[];
  top_recommendations: Array<{
    dimension: string;
    text: string;
    titel?: string;
    priority: "hoch" | "mittel";
    // Erweiterte Felder für PDF
    beschreibung?: string;
    schritte?: string[];
    beispiel?: string;
    erwartetes_ergebnis?: string;
    aufwand?: string;
    wirkung?: string;
    zeitaufwand?: string;
    prioritaet?: string;
    kriterium?: string;
  }>;
  meta: {
    load_time_ms: number;
    status_code: number;
    page_size_kb: number;
    title: string;
    domain: string;
  };
};

function ScoreBar({ score, max, color }: { score: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  return (
    <div className="w-full bg-gray-200 h-2 overflow-hidden">
      <div style={{ width: `${pct}%`, backgroundColor: color, height: "100%", transition: "width 0.6s ease" }} />
    </div>
  );
}

function getGradeColor(grade: string): string {
  if (grade === "A+" || grade === "A") return "#16a34a";
  if (grade === "B") return "#b45309";
  if (grade === "C") return "#ca8a04";
  if (grade === "D") return "#ea580c";
  return "#dc2626";
}

function getScoreColor(pct: number): string {
  if (pct >= 70) return "#16a34a";
  if (pct >= 45) return "#ca8a04";
  return "#dc2626";
}

function getGradeLabel(grade: string): string {
  if (grade === "A+" || grade === "A") return "Exzellente Conversion-Optimierung";
  if (grade === "B") return "Gute Conversion-Basis";
  if (grade === "C") return "Ausbaufähiges Conversion-Potenzial";
  if (grade === "D") return "Schwache Conversion-Signale";
  return "Kritischer Conversion-Bedarf";
}

function LoadingScreen({ url }: { url: string }) {
  const steps = [
    { label: "Website auf Mobile laden", icon: "/manus-storage/33_d51b7819.svg" },
    { label: "Persuasions-Signale prüfen (Cialdini)", icon: "/manus-storage/11_094992a3.svg" },
    { label: "Trust & Vertrauen analysieren", icon: "/manus-storage/29_7f3bb394.svg" },
    { label: "CTAs & Value Proposition bewerten", icon: "/manus-storage/icon_cta_value_9a48b0a2.png" },
    { label: "UX-Reibung messen", icon: "/manus-storage/33_d51b7819.svg" },
    { label: "Dringlichkeit & Preispsychologie", icon: "/manus-storage/icon_dringlichkeit_3419dec4.png" },
    { label: "Content & Lesbarkeit prüfen", icon: "/manus-storage/icon_content_lesbarkeit_9425589a.png" },
    { label: "Micro-Conversions auswerten", icon: "/manus-storage/32_04885248.svg" },
    { label: "Conversion-Score berechnen", icon: "/manus-storage/32_04885248.svg" },
  ];
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((s) => (s + 1) % steps.length);
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
      <div style={{ height: "4px", backgroundColor: C.amber, position: "fixed", top: 0, left: 0, right: 0 }} />
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: C.amberLight }}>
            <span className="text-4xl">📈</span>
          </div>
          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.amber, fontFamily: "'DM Sans', sans-serif" }}>
            Conversion-Barometer™ analysiert
          </p>
          <h2 className="font-black uppercase text-2xl mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Deine Website wird geprüft
          </h2>
          <p className="text-gray-500 text-sm truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{url}</p>
        </div>
        <div className="space-y-3 text-left mb-8">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center gap-3 p-3"
              style={{
                backgroundColor: i === activeStep ? C.amberLight : "transparent",
                borderLeft: i === activeStep ? `3px solid ${C.amber}` : "3px solid transparent"
              }}>
              <img src={step.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
              <span className="text-sm font-medium" style={{ color: i <= activeStep ? C.text : C.muted, fontFamily: "'DM Sans', sans-serif" }}>{step.label}</span>
              {i < activeStep && <span className="ml-auto text-green-600 text-sm">✓</span>}
              {i === activeStep && <span className="ml-auto text-xs animate-pulse" style={{ color: C.amber }}>läuft …</span>}
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Die Analyse dauert ca. 30–60 Sekunden. Bitte nicht schließen.
        </p>
      </div>
    </div>
  );
}

function ConversionResultsView({ data, onReset }: { data: ConversionData; onReset: () => void }) {
  // Defensive Normalisierung: alle Felder absichern
  const d: ConversionData = {
    url: data?.url ?? '',
    branche: data?.branche ?? '',
    total_score: Number(data?.total_score ?? 0),
    max_score: Number(data?.max_score ?? 100),
    grade: data?.grade ?? 'F',
    percentage: Number(data?.percentage ?? 0),
    screenshot_b64: data?.screenshot_b64 ?? null,
    dimensions: Array.isArray(data?.dimensions) ? data.dimensions.map(dim => ({
      name: String(dim?.name ?? ''),
      score: Number(dim?.score ?? 0),
      max: Number(dim?.max ?? 25),
      icon: String(dim?.icon ?? ''),
      findings: Array.isArray(dim?.findings) ? dim.findings.map(String) : [],
      recommendations: Array.isArray(dim?.recommendations) ? dim.recommendations.map(String) : [],
    })) : [],
    top_recommendations: Array.isArray(data?.top_recommendations) ? data.top_recommendations.map(r => ({
      dimension: String(r?.dimension ?? ''),
      text: String(r?.text ?? ''),
      priority: (r?.priority === 'hoch' ? 'hoch' : 'mittel') as 'hoch' | 'mittel',
    })) : [],
    meta: {
      load_time_ms: Number(data?.meta?.load_time_ms ?? 0),
      status_code: Number(data?.meta?.status_code ?? 200),
      page_size_kb: Number(data?.meta?.page_size_kb ?? 0),
      title: String(data?.meta?.title ?? ''),
      domain: String(data?.meta?.domain ?? ''),
    },
  };
  const [activeTab, setActiveTab] = useState<"uebersicht" | "dimensionen" | "empfehlungen">("uebersicht");
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (d.percentage / 100) * circumference;
  const gradeColor = getGradeColor(d.grade);

  return (
    <div className="min-h-screen bg-white text-black">
      <div style={{ height: "4px", backgroundColor: C.amber }} />
      <BarometerNav current="/conversion-barometer" accentColor={"#b45309"} />

      <div className="px-6 py-3 flex items-center gap-3" style={{ backgroundColor: C.bgGray, borderBottom: `1px solid ${C.border}` }}>
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "#16a34a" }} />
        <span className="text-gray-500 text-xs uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>Analysiert:</span>
        <span className="text-black text-sm font-medium truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.url}</span>
        {d.meta.title && <span className="text-gray-400 text-xs hidden md:block">· {d.meta.title}</span>}
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Score-Übersicht */}
        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <div className="border border-gray-200 p-8 flex flex-col items-center justify-center text-center" style={{ backgroundColor: C.bgGray }}>
            <div className="relative mb-6">
              <svg className="w-40 h-40 -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r={radius} fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="8" />
                <circle cx="60" cy="60" r={radius} fill="none" stroke={gradeColor} strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-5xl font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gradeColor }}>{d.percentage}</span>
                <span className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>von 100</span>
              </div>
            </div>
            <p className="text-sm font-bold mb-1" style={{ color: gradeColor, fontFamily: "'DM Sans', sans-serif" }}>
              {d.percentage >= 80 ? "Exzellente Conversion-Optimierung" : d.percentage >= 60 ? "Gute Conversion-Basis" : d.percentage >= 40 ? "Ausbaufähiges Conversion-Potenzial" : d.percentage >= 20 ? "Schwache Conversion-Signale" : "Kritischer Conversion-Bedarf"}
            </p>
            <p className="text-xs text-gray-500 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {d.total_score} von {d.max_score} Punkten
            </p>
            <div className="grid grid-cols-2 gap-3 w-full text-left">
              <div className="p-3 bg-white border border-gray-200">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Ladezeit (Mobile)</p>
                <p className="font-black text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif", color: d.meta.load_time_ms <= 2000 ? "#16a34a" : d.meta.load_time_ms <= 4000 ? "#ca8a04" : "#dc2626" }}>
                  {(d.meta.load_time_ms / 1000).toFixed(1)}s
                </p>
              </div>
              <div className="p-3 bg-white border border-gray-200">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Domain</p>
                <p className="font-bold text-xs truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.meta.domain}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {d.screenshot_b64 && (
              <div className="border border-gray-200 overflow-hidden">
                <img src={`data:image/jpeg;base64,${d.screenshot_b64}`} alt="Website Screenshot (Mobile)" className="w-full object-cover" style={{ maxHeight: 200 }} />
              </div>
            )}
            <div className="border border-gray-200 p-5" style={{ backgroundColor: C.bgGray }}>
              <p className="text-[10px] uppercase tracking-widest font-bold mb-4" style={{ color: C.amber, fontFamily: "'DM Sans', sans-serif" }}>8 Dimensionen auf einen Blick</p>
              <div className="space-y-2">
                {d.dimensions.map((dim) => {
                  const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
                  return (
                    <div key={dim.name} className="flex items-center gap-2">
                      <img src={dim.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                      <span className="text-xs flex-1 truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dim.name}</span>
                      <span className="text-xs font-bold w-14 text-right" style={{ color: getScoreColor(dp), fontFamily: "'DM Sans', sans-serif" }}>{dim.score}/{dim.max}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Branchenkontext-Hinweis */}
        {d.branche && d.branche !== "" && (
          <div className="mb-8 p-4 border-l-4" style={{ borderColor: C.amber, backgroundColor: C.amberLight }}>
            <p className="text-xs font-black uppercase tracking-widest mb-1" style={{ color: C.amberDark, fontFamily: "'DM Sans', sans-serif" }}>
              📊 Branchenkontext: {d.branche.charAt(0).toUpperCase() + d.branche.slice(1)}
            </p>
            <p className="text-xs text-gray-600 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Conversion-Benchmarks variieren je nach Branche erheblich. E-Commerce-Shops erzielen typischerweise 1–3 %, SaaS-Anbieter 3–5 %, Dienstleister bis zu 10 % Conversion Rate. Branchenspezifische Besonderheiten (z. B. Kaufentscheidungsdauer, Vertrauensanforderungen, mobile Nutzungsverhalten) können deinen Score positiv oder negativ beeinflussen.
            </p>
          </div>
        )}
        {!d.branche && (
          <div className="mb-8 p-4 border-l-4" style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb' }}>
            <p className="text-xs font-black uppercase tracking-widest mb-1 text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              📊 Hinweis zur Interpretation
            </p>
            <p className="text-xs text-gray-500 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Conversion-Benchmarks variieren stark nach Branche. Gib beim nächsten Scan deine Branche an, um branchenspezifische Einordnung zu erhalten.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-8 overflow-x-auto">
          {(["uebersicht", "dimensionen", "empfehlungen"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
              style={{
                color: activeTab === tab ? C.amber : C.muted,
                borderBottom: activeTab === tab ? `2px solid ${C.amber}` : "2px solid transparent",
                fontFamily: "'DM Sans', sans-serif"
              }}>
              {tab === "uebersicht" ? "📊 Übersicht" : tab === "dimensionen" ? "🔍 Dimensionen" : "💡 Empfehlungen"}
            </button>
          ))}
        </div>

        {/* Tab: Übersicht */}
        {activeTab === "uebersicht" && (
          <div className="space-y-4">
            {d.dimensions.map((dim) => {
              const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
              const dc = getScoreColor(dp);
              return (
                <div key={dim.name} className="border border-gray-200 p-5" style={{ backgroundColor: C.bgGray }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <img src={dim.icon} alt="" className="w-6 h-6 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                      <span className="font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dim.name}</span>
                    </div>
                    <span className="text-sm font-black ml-4 whitespace-nowrap" style={{ color: dc, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.score}/{dim.max}</span>
                  </div>
                  <ScoreBar score={dim.score} max={dim.max} color={dc} />
                </div>
              );
            })}
          </div>
        )}

        {/* Tab: Dimensionen */}
        {activeTab === "dimensionen" && (
          <div className="space-y-6">
            {d.dimensions.map((dim) => {
              const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
              const dc = getScoreColor(dp);
              return (
                <div key={dim.name} className="border border-gray-200">
                  <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: C.bgGray, borderBottom: `1px solid ${C.border}` }}>
                    <div className="flex items-center gap-3">
                      <img src={dim.icon} alt="" className="w-6 h-6 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                      <span className="font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dim.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dp}%</span>
                      <span className="font-black text-lg" style={{ color: dc, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.score}/{dim.max}</span>
                    </div>
                  </div>
                  <div className="px-6 py-5">
                    <ScoreBar score={dim.score} max={dim.max} color={dc} />
                    {dim.findings.length > 0 && (
                      <div className="mt-4">
                        <p className="text-[10px] uppercase tracking-widest font-bold mb-2 text-green-700" style={{ fontFamily: "'DM Sans', sans-serif" }}>✓ Positiv</p>
                        <ul className="space-y-1">
                          {dim.findings.map((f, i) => (
                            <li key={i} className="text-xs text-gray-700 flex items-start gap-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                              <span className="text-green-600 mt-0.5 shrink-0">✓</span>
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {dim.recommendations.length > 0 && (
                      <div className="mt-4">
                        <p className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.amber, fontFamily: "'DM Sans', sans-serif" }}>→ Handlungsempfehlungen</p>
                        <ul className="space-y-2">
                          {dim.recommendations.map((r, i) => (
                            <li key={i} className="text-xs text-gray-700 flex items-start gap-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                              <span className="shrink-0 mt-0.5" style={{ color: C.amber }}>→</span>
                              <span>{r}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tab: Empfehlungen */}
        {activeTab === "empfehlungen" && (
          <div className="space-y-4">
            <div className="border-l-4 p-4 mb-6" style={{ borderColor: C.amber, backgroundColor: C.amberLight }}>
              <p className="text-sm font-bold mb-1" style={{ color: C.amberDark, fontFamily: "'DM Sans', sans-serif" }}>
                Top-{d.top_recommendations.length} Maßnahmen für mehr Conversions
              </p>
              <p className="text-xs text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Priorisiert nach wissenschaftlichen Erkenntnissen (Cialdini, Baymard Institute, Nielsen Norman Group, Fogg Behavior Model)
              </p>
            </div>
            {d.top_recommendations.map((rec, i) => (
              <div key={i} className="border border-gray-200 p-5 flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center shrink-0 font-black text-sm text-white"
                  style={{ backgroundColor: rec.priority === "hoch" ? C.amber : C.muted, fontFamily: "'Bebas Neue', sans-serif" }}>
                  {i + 1}
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: rec.priority === "hoch" ? C.amber : C.muted, fontFamily: "'DM Sans', sans-serif" }}>
                    {rec.dimension} · Priorität: {rec.priority}
                  </p>
                  <p className="text-sm text-gray-800" style={{ fontFamily: "'DM Sans', sans-serif" }}>{rec.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 border border-gray-200 p-8 text-center" style={{ backgroundColor: C.bgGray }}>
          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>
          <AgencyLogo size="sm" />
          </p>
          <h3 className="font-black uppercase text-2xl mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Du willst diese Empfehlungen umsetzen?
          </h3>
          <p className="text-gray-500 text-sm mb-6 max-w-lg mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
Die identifizierten Conversion-Hebel können professionell umgesetzt werden – mit konkreten Maßnahmen, die messbar mehr Anfragen und Käufe bringen.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => generateConversionPdf(d as unknown as Record<string, unknown>)}
              className="text-white font-bold text-sm uppercase tracking-widest px-8 py-4 transition-colors"
              style={{ backgroundColor: '#b45309', fontFamily: "'DM Sans', sans-serif" }}>
              📄 PDF-Report herunterladen
            </button>
            <a href="mailto:info@werbeagentur-larissa-girenko.de"
              className="inline-block text-white font-bold text-sm uppercase tracking-widest px-8 py-4"
              style={{ backgroundColor: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>
              Jetzt Beratung anfragen →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConversionHome() {
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  useEffect(() => applySeo({
    title: "Conversion-Barometer™ – Warum kaufen Besucher nicht? | WERBEAGENTUR Larissa Girenko",
    description: "Deine Website hat Besucher, aber keine Anfragen? Conversion-Barometer™ analysiert 100+ Conversion-Faktoren. Vollanalyse ab 97 € – Kostenlose Vorschau sofort.",
    path: "/conversion-barometer",
  }), []);
  const [url, setUrl] = useState("");
  const [branche, setBranche] = useState("");
  const [result, setResult] = useState<ConversionData | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [previewResult, setPreviewResult] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [analyzingUrl, setAnalyzingUrl] = useState("");

  const analyze = trpc.conversionBarometer.analyze.useMutation({
    onSuccess: (res) => {
      setIsLoading(false);
      if (res.success && res.data) {
        const raw = res.data as Record<string, unknown>;

        // Icon-Mapping für Conversion-Dimensionen
        const dimensionIcons: Record<string, string> = {
          call_to_action: '/manus-storage/icon_cta_value_9a48b0a2.png',
          vertrauen: '/manus-storage/29_7f3bb394.svg',
          nutzerfreundlichkeit: '/manus-storage/33_d51b7819.svg',
          angebotskommunikation: '/manus-storage/11_094992a3.svg',
        };

        // Server gibt 'scores' zurück, Frontend erwartet 'dimensions'
        const scoresRaw = (raw.scores ?? {}) as Record<string, {
          label?: string; score?: number; max?: number;
          details?: Record<string, { score?: number; max?: number; found?: boolean; count?: number; ms?: number }>;
        }>;
        const dimensions: ConversionDimension[] = Object.entries(scoresRaw).map(([key, dim]) => ({
          name: String(dim.label ?? key),
          score: Number(dim.score ?? 0),
          max: Number(dim.max ?? 25),
          icon: dimensionIcons[key] ?? '/manus-storage/32_04885248.svg',
          findings: Object.entries(dim.details ?? {}).map(([k, d]) => {
            const found = d.found !== false && (d.score ?? 0) > 0;
            // Lesbare deutsche Bezeichnungen für Kriterien
            const labelMap: Record<string, string> = {
              cta_sichtbar: 'CTA sichtbar', cta_above_fold: 'CTA im sichtbaren Bereich', cta_klarheit: 'CTA-Klarheit',
              cta_vielfalt: 'CTA-Vielfalt', testimonials: 'Testimonials / Bewertungen', zertifikate: 'Zertifikate / Auszeichnungen',
              impressum_datenschutz: 'Impressum & Datenschutz', kontaktinfos: 'Kontaktinformationen', ssl_sicherheit: 'SSL-Sicherheit',
              navigation_klar: 'Klare Navigation', mobile_optimiert: 'Mobile Optimierung', ladezeit: 'Ladezeit',
              suchfunktion: 'Suchfunktion', unique_value_proposition: 'Alleinstellungsmerkmal (UVP)',
              preise_sichtbar: 'Preise sichtbar', leistungen_klar: 'Leistungen klar', zielgruppe_angesprochen: 'Zielgruppenansprache',
            };
            const label = labelMap[k] ?? k.replace(/_/g, ' ');
            return `${label}: ${found ? '\u2713 vorhanden' : '\u2717 fehlt'} (${d.score ?? 0}/${d.max ?? 0} Pkt.)`;
          }),
          recommendations: Object.entries(dim.details ?? {})
            .filter(([, d]) => d.found === false || (d.score ?? 0) === 0)
            .map(([k]) => `${k.replace(/_/g, ' ')} optimieren`),
        }));

        // top_recommendations aus optimierungen mappen - alle Felder für PDF übertragen
        const optis = (raw.top_recommendations ?? raw.optimierungen ?? []) as Array<Record<string, unknown>>;
        const top_recommendations = optis.map((o) => ({
          dimension: String(o.dimension ?? o.kriterium ?? ''),
          // 'text' ist das Frontend-Feld für den Titel, 'titel' wird von der PDF-Funktion gelesen
          text: String(o.titel ?? o.tipp ?? o.text ?? o.empfehlung ?? o.kriterium ?? ''),
          titel: String(o.titel ?? o.tipp ?? o.text ?? o.empfehlung ?? o.kriterium ?? ''),
          priority: (o.prioritaet === 'hoch' || o.priority === 'hoch' || o.prioritaet === 'kritisch' ? 'hoch' : 'mittel') as 'hoch' | 'mittel',
          // Alle erweiterten Felder für ausführliche PDF-Darstellung
          beschreibung: o.beschreibung ? String(o.beschreibung) : undefined,
          schritte: Array.isArray(o.schritte) ? (o.schritte as unknown[]).map(String) : undefined,
          beispiel: o.beispiel ? String(o.beispiel) : undefined,
          erwartetes_ergebnis: o.erwartetes_ergebnis ? String(o.erwartetes_ergebnis) : undefined,
          aufwand: o.aufwand ? String(o.aufwand) : undefined,
          wirkung: o.wirkung ? String(o.wirkung) : undefined,
          zeitaufwand: o.zeitaufwand ? String(o.zeitaufwand) : undefined,
          prioritaet: o.prioritaet ? String(o.prioritaet) : undefined,
          kriterium: o.kriterium ? String(o.kriterium) : undefined,
        }));

        // meta aus verfügbaren Feldern zusammenbauen
        const loadMs = Number(
          (scoresRaw.nutzerfreundlichkeit?.details?.ladezeit as Record<string, unknown> | undefined)?.ms ?? 0
        );
        const meta = {
          load_time_ms: loadMs,
          status_code: Number(raw.status_code ?? 200),
          page_size_kb: Number(raw.page_size_kb ?? 0),
          title: String(raw.page_title ?? raw.title ?? ''),
          domain: String(raw.domain ?? ''),
        };

        const totalScore = Number(raw.total_score ?? 0);
        const maxScore = 100;
        const percentage = Math.round((totalScore / maxScore) * 100);
        const grade = percentage >= 90 ? 'A+' : percentage >= 80 ? 'A' : percentage >= 70 ? 'B' : percentage >= 60 ? 'C' : percentage >= 50 ? 'D' : 'F';

        const mapped: ConversionData = {
          url: String(raw.url ?? ''),
          branche: String(raw.branche ?? ''),
          total_score: totalScore,
          max_score: maxScore,
          grade,
          percentage,
          screenshot_b64: raw.screenshot_b64 as string | null ?? null,
          dimensions,
          top_recommendations,
          meta,
        };
        setResult(mapped);
        setErrorMsg("");
      } else {
        setIsLoading(false);
        setErrorMsg(res.error || "Analyse fehlgeschlagen. Bitte versuche es erneut.");
      }
    },
    onError: (err) => {
      setIsLoading(false);
      setErrorMsg(err.message || "Unbekannter Fehler. Bitte versuche es erneut.");
    },
  });
  // Preview-Mutation für kostenlose Vorschau (nur Scores)
  const previewMutation = trpc.conversionBarometer.analyzePreview.useMutation({
    onSuccess: (res) => {
      setIsLoading(false);
      if (res.success && res.data) {
        setPreviewResult(res.data as Record<string, unknown>);
      } else {
        setErrorMsg((res as Record<string, unknown>).error as string || "Unbekannter Fehler");
      }
    },
    onError: (err) => { setIsLoading(false); setErrorMsg(err.message); },
  });

  const handleSubmit = () => {
    if (!url.trim()) return;
    setErrorMsg("");
    setResult(null);
    setPreviewResult(null);
    setAnalyzingUrl(url.trim());
    setIsLoading(true);
    if (isAdminUser) {
      analyze.mutate({ url: url.trim(), branche: branche || undefined });
    } else {
      previewMutation.mutate({ url: url.trim() });
    }
  };
  const createCheckout = trpc.payment.createCheckout.useMutation({
    onSuccess: (data) => {
      if (data.checkoutUrl) window.open(data.checkoutUrl, "_blank");
    },
    onError: (err) => toast.error(err.message),
  });
  const handleBuy = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "conversion_analyse",
      targetUrl: (previewResult as Record<string, unknown>)?.url as string || url.trim() || undefined,
      returnPath: "/conversion-barometer",
    });
  };
  const handleBuyVideoCall = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "conversion_mit_videocall",
      targetUrl: (previewResult as Record<string, unknown>)?.url as string || url.trim() || undefined,
      returnPath: "/conversion-barometer",
    });
  };
  const handleReset = () => { setResult(null); setPreviewResult(null); setIsLoading(false); setUrl(""); setErrorMsg(""); analyze.reset(); previewMutation.reset(); };
  const handleBooking = () => {
    const bookingUrl = url.trim()
      ? `${MEETERGO_URL}&notes=${encodeURIComponent("Website: " + url.trim())}`
      : MEETERGO_URL;
    window.open(bookingUrl, "_blank", "noopener,noreferrer");
  };
  if (isLoading) return (
    <div className="min-h-screen bg-white flex flex-col">
      <div style={{ height: "4px", backgroundColor: C.amber }} />
      <LoadingScreen url={analyzingUrl} />
    </div>
  );
  if (result) return <ConversionResultsView data={result} onReset={handleReset} />;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* CI-Akzentlinie */}
      <div style={{ height: "4px", backgroundColor: C.amber }} />

      {/* Header */}
      <header className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-white sticky top-0 z-50">
        <a href="/" className="flex items-center gap-3">
          <img src="/manus-storage/logo_2d439bd1.jpg" alt="WERBEAGENTUR Larissa Girenko" className="w-10 h-10 object-contain" />
          <div>
            <AgencyLogo size="sm" />
            <p className="text-gray-400 text-xs hidden md:block" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Marketing Barometer Suite
            </p>
          </div>
        </a>
        <nav className="flex items-center gap-4">
          <a href="/" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase transition-colors" style={{ fontFamily: "'DM Sans', sans-serif" }}>← Übersicht</a>
          <a href="/sensorik" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase transition-colors hidden sm:inline" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sensorik</a>
          <a href="/lokal" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase transition-colors hidden sm:inline" style={{ fontFamily: "'DM Sans', sans-serif" }}>Lokal</a>
          <a href="/geo-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase transition-colors hidden sm:inline" style={{ fontFamily: "'DM Sans', sans-serif" }}>GEO</a>
          <a href="/seo-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase transition-colors hidden sm:inline" style={{ fontFamily: "'DM Sans', sans-serif" }}>SEO</a>
        </nav>
      </header>

      <BarometerHero
        name="Conversion-Barometer™"
        headline="Besucher kommen,<br />aber kaufen nicht?"
        subtext={`8 wissenschaftliche Dimensionen · Cialdini + Fogg + Kahneman. Kostenlose Vorschau in 60 Sekunden — persönliche Vollanalyse von Larissa ab ${formatPrice(BAROMETER_PRICES.conversion)}.`}
        accentColor={C.amber}
        ctaColor="#115751"
        stats={[
          { val: "8", label: "Dimensionen" },
          { val: "160", label: "Punkte" },
          { val: "60 Sek.", label: "Vorschau" },
        ]}
        url={url}
        onUrlChange={setUrl}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        error={errorMsg}
        isAdmin={isAdminUser}
        adminCtaText="Conversion-Vollanalyse starten →"
      />

      {/* Preview-Ergebnis: nur Scores */}
      {previewResult && (
        <section className="max-w-3xl mx-auto w-full px-6 py-12">
          <div className="border border-gray-200 p-6" style={{ backgroundColor: C.bgGray }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <p className="text-xs font-bold tracking-widest uppercase" style={{ color: C.amber, fontFamily: "'DM Sans', sans-serif" }}>Conversion-Barometer™ Vorschau</p>
              <span className="text-xs text-gray-400 truncate">{analyzingUrl}</span>
            </div>
            {(() => {
              const score = Number((previewResult).total_score ?? 0);
              const maxScore = Number((previewResult).max_score ?? 100);
              const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
              const grade = pct >= 80 ? "A" : pct >= 65 ? "B" : pct >= 45 ? "C" : pct >= 25 ? "D" : "F";
              const gColor = pct >= 70 ? "#16a34a" : pct >= 45 ? "#ca8a04" : "#dc2626";
              return (
                <div className="flex items-center gap-6 mb-6 p-4 bg-white border border-gray-200">
                  <div className="text-center flex-shrink-0">
                    <div className="text-5xl font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gColor }}>{pct}</div>
                    <div className="text-xs text-gray-400">von 100</div>
                  </div>
                  <div>
                    <div className="text-lg font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gColor }}>Note {grade} · {pct >= 80 ? "Starke Conversion" : pct >= 60 ? "Gute Conversion" : pct >= 40 ? "Ausbaufähig" : "Kritisch"}</div>
                    <div className="text-sm text-gray-500 mt-1">{score} von {maxScore} Punkten</div>
                  </div>
                </div>
              );
            })()}
            {(() => {
              const dims = (previewResult.dimension_scores as Array<{ label: string; score: number; max: number }>) ?? [];
              return dims.length > 0 ? (
                <div className="space-y-2 mb-6">
                  <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.amber, fontFamily: "'DM Sans', sans-serif" }}>Dimensionen auf einen Blick</p>
                  {dims.map((d) => {
                    const dp = d.max > 0 ? Math.round((d.score / d.max) * 100) : 0;
                    const dc = dp >= 70 ? "#16a34a" : dp >= 45 ? "#ca8a04" : "#dc2626";
                    return (
                      <div key={d.label} className="flex items-center gap-3">
                        <span className="text-xs w-40 flex-shrink-0" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.label}</span>
                        <div className="flex-1 bg-gray-200 h-2"><div style={{ width: `${dp}%`, backgroundColor: dc, height: "100%" }} /></div>
                        <span className="text-xs font-bold w-8 text-right" style={{ color: dc }}>{dp}%</span>
                      </div>
                    );
                  })}
                </div>
              ) : null;
            })()}
            <div className="border-t border-gray-200 pt-5">
              <p className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Nächster Schritt — Wähle deine Option</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="border-2 border-gray-200 p-5 flex flex-col">
                  <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Alle Befunde, priorisierte Empfehlungen und persönlicher PDF-Report. Lieferung in 72 Stunden.</p>
                  <div className="text-xl font-black mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.amber }}>{formatPrice(BAROMETER_PRICES.conversion)}</div>
                  <button onClick={handleBuy} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: C.amber, fontFamily: "'DM Sans', sans-serif" }}>Analyse buchen</button>
                </div>
                <div className="border-2 p-5 flex flex-col relative" style={{ borderColor: "#6d0000", backgroundColor: "#6d000008" }}>
                  <div className="absolute -top-3 left-4 px-3 py-1 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
                  <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Vollständige Analyse + 30-minütige persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
                  <div className="text-xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#6d0000" }}>{formatPrice(BAROMETER_MIT_VIDEOCALL)}</div>
                  <p className="text-xs text-gray-400 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 30-Min-VideoCall (+79 €)</p>
                  <button onClick={handleBuyVideoCall} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Analyse + VideoCall buchen</button>
                </div>
              </div>
              <button onClick={handleReset} className="block w-full text-center text-xs text-gray-400 mt-2 hover:text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>Andere URL analysieren</button>
            </div>
          </div>
        </section>
      )}


      {/* Was analysiert wird */}
      <section className="border-t border-gray-200 py-16 px-6" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.amber, fontFamily: "'DM Sans', sans-serif" }}>
              8 wissenschaftliche Dimensionen
            </p>
            <h2 className="font-black uppercase leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 5vw, 3rem)" }}>
              Was das Conversion-Barometer prüft
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: "/manus-storage/11_094992a3.svg", title: "Persuasion & Psychologie", desc: "Cialdinis 7 Prinzipien: Reziprozität, Social Proof, Autorität, Knappheit, Konsistenz, Sympathie, Einheit" },
              { icon: "/manus-storage/29_7f3bb394.svg", title: "Vertrauen & Trust", desc: "SSL, Kontaktdaten, Impressum, Bewertungen, Garantien – Baymard Institute Standards" },
              { icon: "/manus-storage/icon_cta_value_9a48b0a2.png", title: "CTA & Value Proposition", desc: "H1 above fold, CTA-Kontrast, Benefit-Kommunikation, Video-Content" },
              { icon: "/manus-storage/33_d51b7819.svg", title: "Reibungsfreiheit & UX", desc: "Fogg Behavior Model: Motivation, Fähigkeit, Auslöser – Formulare, Navigation, Chat" },
              { icon: "/manus-storage/33_d51b7819.svg", title: "Mobile Conversion", desc: "Mobile-First Analyse: Ladezeit, Viewport, Lazy Loading, Hamburger-Menü" },
              { icon: "/manus-storage/icon_dringlichkeit_3419dec4.png", title: "Dringlichkeit & Preispsychologie", desc: "Kahneman Prospect Theory, Anchoring, Scarcity, Countdown, FOMO-Effekte" },
              { icon: "/manus-storage/icon_content_lesbarkeit_9425589a.png", title: "Content & Lesbarkeit", desc: "Nielsen Norman Eye-Tracking: Satzlänge, Struktur, Du-Ansprache, FAQ" },
              { icon: "/manus-storage/32_04885248.svg", title: "Micro-Conversions", desc: "Newsletter, Lead Magnets, Social Media, Testimonials, Engagement-Signale" },
            ].map((item) => (
              <div key={item.title} className="bg-white border border-gray-200 p-5">
                <img src={item.icon} alt="" className="w-7 h-7 mb-3 object-contain" style={{ opacity: 0.85 }} />
                <p className="font-bold text-sm mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.title}</p>
                <p className="text-xs text-gray-500 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Larissa-Sektion */}
      <section className="border-t border-gray-200 py-16 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Portrait */}
            <div className="relative">
              <div className="relative overflow-hidden" style={{ maxHeight: "480px" }}>
                <img
                  src={PORTRAIT_IMG}
                  alt="Larissa Girenko – Conversion-Expertin"
                  className="w-full object-cover object-top"
                  style={{ maxHeight: "480px" }}
                />
              </div>
            </div>
            {/* Text */}
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.amber, fontFamily: "'DM Sans', sans-serif" }}>
                Gegründet & entwickelt von
              </p>
              <h2 className="font-black uppercase leading-none mb-5 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)" }}>
                Larissa Girenko
              </h2>
              <p className="text-gray-300 text-sm leading-relaxed mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
Websites verlieren täglich Umsatz durch psychologische Fehler – unsichtbare Barrieren, die Besucher davon abhalten, zu kaufen oder anzufragen. Der Conversion-Barometer macht diese Schwachstellen messbar.
              </p>
              <p className="text-gray-400 text-sm leading-relaxed mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
Basierend auf den Erkenntnissen von Cialdini, Kahneman, dem Baymard Institute und der Nielsen Norman Group – ein messbares System, das zeigt, wie Websites wirklich auf das Unterbewusstsein wirken.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "8", label: "Dimensionen" },
                  { value: "160", label: "Punkte gesamt" },
                  { value: "Cialdini", label: "Wissenschaftliche Basis" },
                  { value: formatPrice(BAROMETER_PRICES.conversion), label: "Vollversion" },
                ].map((stat) => (
                  <div key={stat.label} className="border-l-2 pl-4" style={{ borderColor: C.amber }}>
                    <p className="text-white font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{stat.value}</p>
                    <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Selling */}
      <CrossSelling
        headline="Conversion optimiert – jetzt auch sichtbar werden"
        subline="Mehr Besucher + bessere Conversion = maximaler Umsatz. Diese Barometer ergänzen sich perfekt."
        items={[
          {
            icon: "/manus-storage/11_094992a3.svg",
            title: "Sensorik-Barometer™",
            description: "Wie wirkt deine Website auf das Unterbewusstsein? Sensorik ist die Basis jeder Conversion.",
            price: formatPrice(BAROMETER_PRICES.sensorik),
            href: "/sensorik",
            accent: "#b45309",
            badge: "Basis der Conversion",
          },
          {
            icon: "/manus-storage/23_1ab7147e.svg",
            title: "SEO-Barometer™",
            description: "Mehr organischer Traffic für deine optimierte Website – 100+ SEO-Kriterien.",
            price: formatPrice(BAROMETER_PRICES.seo),
            href: "/seo-barometer",
            accent: "#0a5c36",
          },
          {
            icon: "/manus-storage/21_453003f0.svg",
            title: "E-Commerce-Barometer™",
            description: "Für Online-Shops: 120+ Kriterien für mehr Vertrauen, höhere Conversion und mehr Umsatz.",
            price: formatPrice(BAROMETER_PRICES.ecommerce),
            href: "/ecommerce-barometer",
            accent: "#b45309",
          },
        ]}
      />

      {/* Footer */}
      <footer className="border-t border-gray-200 px-6 py-8" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AgencyLogo withLogo size="sm" />
          </div>
          <div className="flex items-center gap-6">
            <a href="/" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Übersicht</a>
            <a href="/sensorik" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sensorik</a>
            <a href="/lokal" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Lokal</a>
            <a href="/geo-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>GEO</a>
            <a href="/seo-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>SEO</a>
            <a href="/impressum" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Impressum</a>
            <a href="/datenschutz" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Datenschutz</a>
          </div>
          <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>© 2026 Werbeagentur Larissa Girenko</p>
        </div>
      </footer>
    </div>
  );
}
